import * as THREE from 'three';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter.js';
import { OBJExporter } from 'three/examples/jsm/exporters/OBJExporter.js';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import { CadifyError, ErrorTypes } from './errors.js';

// Utility to convert data to ArrayBuffer
const toArrayBuffer = (data) => {
  if (data instanceof ArrayBuffer) return data;
  if (ArrayBuffer.isView(data)) {
    return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
  }
  if (typeof data === 'string') {
    return new TextEncoder().encode(data).buffer;
  }
  throw new TypeError('Unsupported data type');
};

// Download blob helper
const downloadBlob = (data, filename, mime = 'application/octet-stream') => {
  try {
    const blob = data instanceof Blob ? data : new Blob([data], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
    return { success: true, url };
  } catch (error) {
    throw new CadifyError('Download failed', ErrorTypes.EXPORT, error);
  }
};

// Export to STL (binary)
export const exportSTL = (object, filename = 'model.stl', binary = true) => {
  return new Promise((resolve, reject) => {
    try {
      if (!object || (object.children && object.children.length === 0)) {
        throw new Error('No geometry to export');
      }
      
      const exporter = new STLExporter();
      const result = exporter.parse(object, { binary });
      
      const arrayBuffer = toArrayBuffer(result);
      const blob = new Blob([arrayBuffer], { type: 'application/sla' });
      
      const downloadResult = downloadBlob(blob, filename, 'application/sla');
      resolve(downloadResult);
    } catch (error) {
      reject(new CadifyError('STL export failed', ErrorTypes.EXPORT, error));
    }
  });
};

// Export to OBJ
export const exportOBJ = (object, filename = 'model.obj') => {
  return new Promise((resolve, reject) => {
    try {
      if (!object || (object.children && object.children.length === 0)) {
        throw new Error('No geometry to export');
      }
      
      const exporter = new OBJExporter();
      const result = exporter.parse(object);
      
      const blob = new Blob([result], { type: 'text/plain' });
      const downloadResult = downloadBlob(blob, filename, 'text/plain');
      resolve(downloadResult);
    } catch (error) {
      reject(new CadifyError('OBJ export failed', ErrorTypes.EXPORT, error));
    }
  });
};

// Export to GLTF/GLB
export const exportGLTF = (object, filename = 'model.gltf', binary = false) => {
  return new Promise((resolve, reject) => {
    try {
      if (!object || (object.children && object.children.length === 0)) {
        throw new Error('No geometry to export');
      }
      
      const exporter = new GLTFExporter();
      
      exporter.parse(
        object,
        (result) => {
          try {
            let blob;
            const ext = binary ? '.glb' : '.gltf';
            const finalFilename = filename.replace(/\.(gltf|glb)$/i, '') + ext;
            
            if (binary) {
              blob = new Blob([result], { type: 'application/octet-stream' });
            } else {
              const output = JSON.stringify(result, null, 2);
              blob = new Blob([output], { type: 'application/json' });
            }
            
            const downloadResult = downloadBlob(
              blob,
              finalFilename,
              binary ? 'application/octet-stream' : 'application/json'
            );
            resolve(downloadResult);
          } catch (error) {
            reject(new CadifyError('GLTF export failed', ErrorTypes.EXPORT, error));
          }
        },
        (error) => {
          reject(new CadifyError('GLTF export failed', ErrorTypes.EXPORT, error));
        },
        { binary }
      );
    } catch (error) {
      reject(new CadifyError('GLTF export failed', ErrorTypes.EXPORT, error));
    }
  });
};

// Export to DXF (2D projection)
export const exportDXF = (paths, filename = 'sketch.dxf', scale = 1) => {
  return new Promise((resolve, reject) => {
    try {
      if (!paths || paths.length === 0) {
        throw new Error('No SVG paths available for DXF export');
      }
      
      const dxf = [];
      const pushPair = (k, v) => dxf.push(`${k}\n${v}\n`);
      
      // DXF header
      pushPair(0, 'SECTION');
      pushPair(2, 'HEADER');
      pushPair(9, '$ACADVER');
      pushPair(1, 'AC1015');
      pushPair(0, 'ENDSEC');
      
      // Entities section
      pushPair(0, 'SECTION');
      pushPair(2, 'ENTITIES');
      
      for (const path of paths) {
        for (const subPath of path.subPaths) {
          const points = subPath.getPoints(64);
          if (!points || points.length < 2) continue;
          
          const closed = subPath.currentPoint?.distanceTo(subPath.getPoint(0)) < 1e-6 || subPath.isClosed();
          
          pushPair(0, 'LWPOLYLINE');
          pushPair(100, 'AcDbEntity');
          pushPair(8, '0');
          pushPair(100, 'AcDbPolyline');
          pushPair(90, closed ? points.length + 1 : points.length);
          pushPair(70, closed ? 1 : 0);
          
          for (let i = 0; i < points.length; i++) {
            const x = points[i].x * scale;
            const y = -points[i].y * scale;
            pushPair(10, x.toFixed(6));
            pushPair(20, y.toFixed(6));
          }
          
          if (closed) {
            const x = points[0].x * scale;
            const y = -points[0].y * scale;
            pushPair(10, x.toFixed(6));
            pushPair(20, y.toFixed(6));
          }
        }
      }
      
      pushPair(0, 'ENDSEC');
      pushPair(0, 'EOF');
      
      const blob = new Blob([dxf.join('')], { type: 'application/dxf' });
      const downloadResult = downloadBlob(blob, filename, 'application/dxf');
      resolve(downloadResult);
    } catch (error) {
      reject(new CadifyError('DXF export failed', ErrorTypes.EXPORT, error));
    }
  });
};

// Export screenshot
export const exportScreenshot = (renderer, filename = 'screenshot.png') => {
  return new Promise((resolve, reject) => {
    try {
      if (!renderer || !renderer.domElement) {
        throw new Error('Invalid renderer');
      }
      
      const url = renderer.domElement.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      
      resolve({ success: true, url });
    } catch (error) {
      reject(new CadifyError('Screenshot export failed', ErrorTypes.EXPORT, error));
    }
  });
};

// Export formats configuration
export const EXPORT_FORMATS = {
  stl: {
    name: 'STL',
    extension: '.stl',
    binary: true,
    exporter: exportSTL,
    description: 'Stereolithography (3D printing)'
  },
  obj: {
    name: 'OBJ',
    extension: '.obj',
    binary: false,
    exporter: exportOBJ,
    description: 'Wavefront OBJ'
  },
  gltf: {
    name: 'GLTF',
    extension: '.gltf',
    binary: false,
    exporter: (obj, name) => exportGLTF(obj, name, false),
    description: 'GL Transmission Format (JSON)'
  },
  glb: {
    name: 'GLB',
    extension: '.glb',
    binary: true,
    exporter: (obj, name) => exportGLTF(obj, name, true),
    description: 'GL Transmission Format (Binary)'
  },
  dxf: {
    name: 'DXF',
    extension: '.dxf',
    binary: false,
    exporter: exportDXF,
    description: 'AutoCAD Drawing Exchange Format (2D)'
  }
};

// Main export function
export const exportModel = async (object, format, filename, options = {}) => {
  const exportConfig = EXPORT_FORMATS[format.toLowerCase()];
  
  if (!exportConfig) {
    throw new CadifyError(`Unsupported export format: ${format}`, ErrorTypes.VALIDATION);
  }
  
  const finalFilename = filename.replace(/\.[^/.]+$/, '') + exportConfig.extension;
  
  try {
    return await exportConfig.exporter(object, finalFilename, options);
  } catch (error) {
    throw new CadifyError(
      `Export to ${exportConfig.name} failed`,
      ErrorTypes.EXPORT,
      error
    );
  }
};

